import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import '../css/AttendenceSheet.css';
import { useNavigate } from 'react-router-dom';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import CustomButton from "./CustomButton";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const STORAGE_KEY = "dcr_punched_attendance_v1";

const DCRPunchedAttendence = () => {
  const navigate = useNavigate();

  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([{ id: "All", first_name: "All", last_name: "" }]);

  // default to All and current month/year
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [monthOptions] = useState([
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ]);
  const [yearOptions, setYearOptions] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(31);

  // Generate Years from 2021 to current
  const generateYears = () => {
    const startYear = 2024;
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) years.push(year);
    setYearOptions(years);
  };

  // Calculate days in selected month/year
  const calculateDaysInMonth = useCallback(() => {
    const days = new Date(selectedYear, selectedMonth, 0).getDate();
    setDaysInMonth(days);
  }, [selectedMonth, selectedYear]);

  // Load cache on mount, otherwise initialize via API
  useEffect(() => {
    generateYears();
    calculateDaysInMonth();

    const cached = (() => {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn("Failed to parse sessionStorage cache", e);
        return null;
      }
    })();

    if (cached) {
      // restore cached UI state (safe defaults fallback)
      setStates(cached.states ?? [{ id: "All", name: "All" }]);
      setDivisions(cached.divisions ?? [{ id: "All", name: "All" }]);
      setEmployees(cached.employees ?? [{ id: "All", first_name: "All", last_name: "" }]);
      setSelectedState(cached.selectedState ?? "All");
      setSelectedDivision(cached.selectedDivision ?? "All");
      setSelectedEmployee(cached.selectedEmployee ?? "All");
      setSelectedMonth(cached.selectedMonth ?? (new Date().getMonth() + 1));
      setSelectedYear(cached.selectedYear ?? new Date().getFullYear());
      setTableData(cached.tableData ?? []);
      setShowContainers(!!cached.showContainers);
      // recalc days after restoring month/year
      // calculateDaysInMonth will run because selectedMonth/Year were set
      return;
    }

    // If no cache, initialize by fetching dropdown lists
    let mounted = true;
    const init = async () => {
      try {
        const [statesRes, divRes, empRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/employees`, { headers: getAuthHeaders() }),
        ]);
        if (!mounted) return;

        setStates([{ id: "All", name: "All" }, ...(Array.isArray(statesRes.data) ? statesRes.data : [])]);
        setDivisions([{ id: "All", name: "All" }, ...(Array.isArray(divRes.data) ? divRes.data : [])]);
        setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...(Array.isArray(empRes.data) ? empRes.data : [])]);

        setSelectedState("All");
        setSelectedDivision("All");
        setSelectedEmployee("All");
      } catch (err) {
        console.error("Error initializing dropdowns:", err);
        if (!mounted) return;
        setError("Failed to load dropdowns");
      }
    };
    init();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  // Save important UI state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      const toCache = {
        states,
        divisions,
        employees,
        selectedState,
        selectedDivision,
        selectedEmployee,
        selectedMonth,
        selectedYear,
        tableData,
        showContainers,
        daysInMonth,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toCache));
    } catch (e) {
      // ignore quota errors silently but log in dev
      if (process.env.NODE_ENV !== "production") console.warn("Could not cache DCR UI state", e);
    }
  }, [
    states,
    divisions,
    employees,
    selectedState,
    selectedDivision,
    selectedEmployee,
    selectedMonth,
    selectedYear,
    tableData,
    showContainers,
    daysInMonth,
  ]);

  // fetchEmployees now supports All (no early return)
  const fetchEmployees = useCallback(async (stateId = "All", divisionName = "All") => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = [];
      if (stateId && stateId !== "All") params.push(`state_id=${encodeURIComponent(stateId)}`);
      if (divisionName && divisionName !== "All") params.push(`division=${encodeURIComponent(divisionName)}`);
      if (params.length) url += `?${params.join("&")}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...(Array.isArray(res.data) ? res.data : [])]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  // When user changes state: update divisions and employees. All is valid.
  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);

    if (stateId === "All") {
      // reset divisions and fetch all employees
      setDivisions(prev => [{ id: "All", name: "All" }, ...prev.filter(d => d.id !== "All")]); // keep existing if any
      await fetchEmployees("All", "All");
      return;
    }

    try {
      const divisionRes = await axios.get(`${BASE_URL}/api/divisions?state_id=${stateId}`, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...(Array.isArray(divisionRes.data) ? divisionRes.data : [])]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }

    // fetch employees for selected state (all divisions)
    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);

    if (divisionId === "All") {
      // if All division selected, fetch employees for the current state or all
      await fetchEmployees(selectedState === "All" ? "All" : selectedState, "All");
      return;
    }

    const division = divisions.find(d => d.id == divisionId);
    const divisionName = division?.division_name || division?.name || null;
    fetchEmployees(selectedState === "All" ? "All" : selectedState, divisionName || "All");
  };

  // Helper: robust getter for day info (supports row.days.day_1 or row.day_1 or numeric keys)
  const getDayInfo = (row, day) => {
    const keys = [
      `day_${day}`,
      `d_${day}`,
      `${day}`
    ];

    const daysObj = row.days || row;

    for (const k of keys) {
      if (daysObj && Object.prototype.hasOwnProperty.call(daysObj, k)) {
        return daysObj[k] ?? null;
      }
    }
    return null;
  };

  // Compute totals for a row
  const computeRowTotals = (row) => {
    let present_days = 0;
    let leave_days = 0;
    let holiday_days = 0;
    let total_pob = 0;
    let total_working_days = 0;

    for (let d = 1; d <= daysInMonth; d++) {
      const info = getDayInfo(row, d);
      if (!info) continue;

      const rawStatus = (info.status ?? info.day_name ?? "").toString().trim();
      const statusLower = rawStatus.toLowerCase();

      if (statusLower === 'p' || statusLower === 'present') {
        present_days++;
        total_working_days++;
      } else if (statusLower === 'a' || statusLower === 'absent') {
        leave_days++;
      } else if (statusLower === 'sunday' || statusLower === 'holiday' || statusLower === 'h') {
        holiday_days++;
      } else if (statusLower === 'w' || statusLower === 'working') {
        total_working_days++;
      }

      const pob = Number(info.pob ?? info.pob_value ?? 0) || 0;
      total_pob += pob;
    }

    return {
      present_days,
      leave_days,
      holiday_days,
      total_pob,
      total_working_days
    };
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);
      let url = `${BASE_URL}/api/reports/employee-attendance-summary?`;
      if (selectedState && selectedState !== "All") url += `state_id=${encodeURIComponent(selectedState)}&`;

      if (selectedDivision && selectedDivision !== "All") {
        const divisionObj = divisions.find(d => d.id == selectedDivision);
        const divisionName = divisionObj?.division_name || divisionObj?.name || selectedDivision;
        url += `division=${encodeURIComponent(divisionName)}&`;
      }

      if (selectedMonth) url += `month=${selectedMonth}&`;
      if (selectedYear) url += `year=${selectedYear}&`;

      url = url.replace(/&$/, '');

      console.log("Calling attendance summary URL:", url);

      const response = await axios.get(url, { headers: getAuthHeaders() });
      console.log("Raw API response:", response);

      let payload = [];
      if (Array.isArray(response.data)) payload = response.data;
      else if (response.data && Array.isArray(response.data.data)) payload = response.data.data;
      else if (response.data && response.data.data && Array.isArray(response.data.data.records)) payload = response.data.data.records;
      else if (response.data && typeof response.data === 'object') {
        const firstArray = Object.values(response.data).find(v => Array.isArray(v));
        payload = firstArray || [];
      }

      console.log("Normalized payload (array):", payload);

      setTableData(payload);
      setShowContainers(true);

      // Persist new table data to sessionStorage immediately
      try {
        const cachedRaw = sessionStorage.getItem(STORAGE_KEY);
        const cached = cachedRaw ? JSON.parse(cachedRaw) : {};
        const toSave = { ...(cached || {}), tableData: payload, showContainers: true };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Failed to update cache after GO", e);
      }
    } catch (err) {
      console.error("Failed to fetch data", err);
      if (err.response) setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
      else if (err.request) setError("No response received from server");
      else setError(`Request error: ${err.message}`);
      setTableData([]);
      setShowContainers(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Render day headers dynamically with day numbers and letters
  const renderDayHeaders = () => {
    const headers = [];
    const dayLetters = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = date.getDay();
      const dayLetter = dayLetters[dayOfWeek];
      const isSunday = dayOfWeek === 0;

      headers.push(
        <th
          key={`day-${day}`}
          className={`AttendenceSheet-day-header ${isSunday ? 'sunday-header' : ''}`}
        >
          <div className="day-number">{day}</div>
          <div className="day-letter">{dayLetter}</div>
        </th>
      );
    }
    return headers;
  };

  // Render day cells for each row (show status)
  const renderDayCells = (row) => {
    const cells = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const info = getDayInfo(row, day);
      const statusText = info ? (info.status ?? info.day_name ?? "") : "";

      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const clickable = info != null;

      cells.push(
        <td
          key={`day-${day}-${row.employee_id || row.id || Math.random()}`}
          className={clickable ? "clickable-day" : ""}
          onClick={() => clickable && handleDayClick(row.employee_id || row.id, dateStr)}
          title={statusText}
        >
          <div>{statusText}</div>
        </td>
      );
    }
    return cells;
  };

  // Updated handleDayClick to navigate to Pobdatewise-attendancedata
  const handleDayClick = (employeeId, dateStr) => {
    navigate(`/admin/Pobdatewise-attendancedata?employee_id=${employeeId}&date=${dateStr}`);
  };

  const uniqueEmployees = Array.isArray(tableData)
    ? Array.from(new Map(tableData.map(emp => [emp.employee_id || emp.id || emp.employeeCode || Math.random(), emp])).values())
    : [];

  const exportToExcel = () => {
    const exportData = uniqueEmployees.map((row, index) => {
      const totals = computeRowTotals(row);
      const rowData = {
        "S.No": index + 1,
        "Emp ID": row.employee_id || row.id || "-",
        "State": row.state || "-",
        "Employee": `${row.employee_name || row.name || '-'}${row.status === "RESIGNED" ? ` (${row.status})` : ''}`,
        "SMS": row.sms || "-",
        "DOJ": row.final_joining_date ? new Date(row.final_joining_date).toLocaleDateString() : (row.doj ? new Date(row.doj).toLocaleDateString() : "-"),
        "DOR": row.resign_date ? new Date(row.resign_date).toLocaleDateString() : (row.dor ? new Date(row.dor).toLocaleDateString() : "-"),
        "Manager": row.manager || "-",
        "Designation": row.designation || "-",
        "City/HQ": row.city_hq || row.city || row.hq || "-",
        "Division": row.division || row.division_name || "-",
        "No. Of days Present (Punched)": totals.present_days || 0,
        "Leaves": totals.leave_days || 0,
        "Total Working Days (DCR-Filled)": totals.total_working_days || 0,
        "P": totals.present_days || 0,
        "T": totals.total_pob || 0,
        "X": totals.leave_days || 0,
        "M": totals.holiday_days || 0,
        "W": row.w || row.W || 0,
        "H": row.h || row.H || 0,
        "S": row.s || row.S || 0,
        "WD": row.wd || row.WD || 0,
        "SL": row.sl || row.SL || 0,
        "CL": row.cl || row.CL || 0,
        "EL": row.el || row.EL || 0,
        "ML": row.ml || row.ML || 0,
        "RL": row.rl || row.RL || 0,
        "CO": row.co || row.CO || 0,
        "ESI": row.esi || row.ESI || 0,
        "L": row.l || row.L || 0,
        "A": row.a || row.A || 0,
        "WW": row.ww || row.WW || 0,
        "AWW": row.aww || row.AWW || 0
      };

      for (let day = 1; day <= daysInMonth; day++) {
        const info = getDayInfo(row, day);
        rowData[`Day ${day}`] = info ? (info.status ?? info.day_name ?? "") : "";
      }

      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Summary");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(data, `Attendance_Summary_${selectedMonth}_${selectedYear}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const tableHtml = document.querySelector('.AttendenceSheet-table-wrapper')?.innerHTML || '';
    const monthName = monthOptions.find(m => m.value === selectedMonth)?.label || '';

    printWindow.document.write(`
      <html>
        <head>
          <title>Attendance Summary Report</title>
          <style>
            @page { size: landscape; margin: 10mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .print-header { margin-bottom: 15px; }
            .print-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .print-filters { font-size: 14px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .sunday-header { background-color: #ffe6e6; }
            .day-number { font-weight: bold; }
            .day-letter { font-size: 0.8em; }
            tr { page-break-inside: avoid; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">Attendance Summary Report</div>
            <div class="print-filters">
              <div>State: ${states.find(s => s.id == selectedState)?.name || 'All'}</div>
              <div>Division: ${divisions.find(d => d.id == selectedDivision)?.division_name || divisions.find(d => d.id == selectedDivision)?.name || 'All'}</div>
              <div>Period: ${monthName} ${selectedYear}</div>
              <div>Generated on: ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
          ${tableHtml}
          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, 300); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="AttendenceSheet-main-container">
      <div className="AttendenceSheet-filter-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700"> DCR </span>
          <span className="text-blue-500">Attendence</span>
        </h1>

        <div className="AttendenceSheet-filter-row1">
          <label className="AttendenceSheet-label">State</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
            required
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="AttendenceSheet-label">Division</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.division_name || division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="AttendenceSheet-filter-row2">
          <label className="AttendenceSheet-label">Year</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(parseInt(e.target.value));
              calculateDaysInMonth();
            }}
            disabled={isLoading}
            required
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <label className="AttendenceSheet-label">Month</label>
          <select
            className="AttendenceSheet-dropdown"
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(parseInt(e.target.value));
              calculateDaysInMonth();
            }}
            disabled={isLoading}
            required
          >
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        <button
          className="AttendenceSheet-go-button"
          onClick={handleGoClick}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "GO"}
        </button>

        {error && <div className="AttendenceSheet-error">{error}</div>}
      </div>

      {showContainers && (
        <div className="AttendenceSheet-table-container">
          <div className="AttendenceSheet-table-wrapper">
            <div className="AttendenceSheet-table-scroll">
              <table className="AttendenceSheet-table">
                <thead className="text-white">
                  <tr>
                    <th rowSpan="2">S.No</th>
                    <th rowSpan="2">Emp ID</th>
                    <th rowSpan="2">State</th>
                    <th rowSpan="2">Employee</th>
                    <th rowSpan="2">SMS</th>
                    <th rowSpan="2">DOJ</th>
                    <th rowSpan="2">DOR</th>
                    <th rowSpan="2">Manager</th>
                    <th rowSpan="2">Designation</th>
                    <th rowSpan="2">City/HQ</th>
                    <th rowSpan="2">Division</th>
                    <th rowSpan="2">No. Of days Present (Punched)</th>
                    <th rowSpan="2">Leaves</th>
                    <th rowSpan="2">Total Working Days (DCR-Filled)</th>
                    {renderDayHeaders()}
                    <th rowSpan="2">P</th>
                    <th rowSpan="2">T</th>
                    <th rowSpan="2">X</th>
                    <th rowSpan="2">M</th>
                    <th rowSpan="2">W</th>
                    <th rowSpan="2">H</th>
                    <th rowSpan="2">S</th>
                    <th rowSpan="2">WD</th>
                    <th rowSpan="2">SL</th>
                    <th rowSpan="2">CL</th>
                    <th rowSpan="2">EL</th>
                    <th rowSpan="2">ML</th>
                    <th rowSpan="2">RL</th>
                    <th rowSpan="2">CO</th>
                    <th rowSpan="2">ESI</th>
                    <th rowSpan="2">L</th>
                    <th rowSpan="2">A</th>
                    <th rowSpan="2">WW</th>
                    <th rowSpan="2">A WW</th>
                    <th rowSpan="2">Tentative Kindly Check Detail from here only</th>
                  </tr>
                </thead>
                <tbody>
                  {uniqueEmployees.length > 0 ? (
                    uniqueEmployees.map((row, index) => {
                      const totals = computeRowTotals(row);

                      return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.employee_id || row.id || '-'}</td>
                          <td>{row.state || '-'}</td>
                          <td>
                            {row.employee_name || row.name || '-'}
                            {row.status === "RESIGNED" && ` (${row.status})`}
                          </td>
                          <td>{row.sms || '-'}</td>
                          <td>
                            {row.final_joining_date
                              ? new Date(row.final_joining_date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric"
                              })
                              : (row.doj ? new Date(row.doj).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                }) : "-")}
                          </td>
                          <td>
                            {row.resign_date
                              ? new Date(row.resign_date).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric"
                              })
                              : (row.dor ? new Date(row.dor).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                }) : "-")}
                          </td>
                          <td>{row.manager || "-"}</td>
                          <td>{row.designation || '-'}</td>
                          <td>{row.city_hq || row.city || row.hq || '-'}</td>
                          <td>{row.division || row.division_name || '-'}</td>
                          <td>{totals.present_days || 0}</td>
                          <td>{totals.leave_days || 0}</td>
                          <td>{totals.total_working_days || 0}</td>

                          {renderDayCells(row)}

                          {/* Summary columns */}
                          <td>{totals.present_days || 0}</td>
                          <td>{totals.total_pob || 0}</td>
                          <td>{totals.leave_days || 0}</td>
                          <td>{totals.holiday_days || 0}</td>
                          <td>{row.w || row.W || 0}</td>
                          <td>{row.h || row.H || 0}</td>
                          <td>{row.s || row.S || 0}</td>
                          <td>{row.wd || row.WD || 0}</td>
                          <td>{row.sl || row.SL || 0}</td>
                          <td>{row.cl || row.CL || 0}</td>
                          <td>{row.el || row.EL || 0}</td>
                          <td>{row.ml || row.ML || 0}</td>
                          <td>{row.rl || row.RL || 0}</td>
                          <td>{row.co || row.CO || 0}</td>
                          <td>{row.esi || row.ESI || 0}</td>
                          <td>{row.l || row.L || 0}</td>
                          <td>{row.a || row.A || 0}</td>
                          <td>{row.ww || row.WW || 0}</td>
                          <td>{row.aww || row.AWW || 0}</td>
                          <td>{row.remarks || ""}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={14 + daysInMonth + 21} className="AttendenceSheet-no-results">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="sob-button-container mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print" />
              <CustomButton onClick={exportToExcel} label="Export" variant="export" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DCRPunchedAttendence;
